import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button, Form, Modal, Row, Col, Badge, InputGroup, Spinner, Alert } from 'react-bootstrap';
import { FaEdit, FaTrash, FaSearch, FaPlus, FaUserCircle, FaEye, FaEyeSlash, FaCheck, FaUpload } from 'react-icons/fa';
import api from '../../../api/apiClient';
import { toast } from 'react-toastify';
import BulkUploadUsersModal from './BulkUploadUsersModal';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  // Update the filter state to handle both status and deleted
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    role: '',
    status: 1,
    group_ids: []
  });
  const [formErrors, setFormErrors] = useState({});
  const [role, setRole] = useState('user'); // Default role
  const [showPassword, setShowPassword] = useState(false);
  const [twofaLoadingUserId, setTwofaLoadingUserId] = useState(null);
  const [groups, setGroups] = useState([]);
  const [roles, setRoles] = useState([]);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);

  // Fetch users from API
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.users.getAll();
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      if (error.response?.status === 401) {
        toast.error('Authentication failed. Please login again.');
      } else {
        toast.error('Failed to load users: ' + (error.response?.data?.message || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggle2fa = async (targetUser) => {
    try {
      if (!targetUser?.id) return;

      const nextEnabled = !Boolean(targetUser.is_2fa_enabled);
      const confirmMsg = nextEnabled
        ? `Enable 2FA for ${getUserDisplayName(targetUser)}? They will be forced to set up authenticator on next login.`
        : `Disable 2FA for ${getUserDisplayName(targetUser)}? This will remove existing 2FA configuration.`;

      // eslint-disable-next-line no-alert
      const confirmed = window.confirm(confirmMsg);
      if (!confirmed) return;

      setTwofaLoadingUserId(targetUser.id);
      await api.users.set2faEnabled(targetUser.id, nextEnabled);
      toast.success(nextEnabled ? '2FA enabled (setup required on next login)' : '2FA disabled');
      await fetchUsers();
    } catch (error) {
      console.error('Error toggling 2FA:', error);
      toast.error(error.response?.data?.message || 'Failed to update 2FA setting');
    } finally {
      setTwofaLoadingUserId(null);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await api.groups.list();
      setGroups(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await api.roles.list();
      setRoles(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchGroups();
    fetchRoles();
  }, []);

  const getRoleName = (roleValue) => {
    const match = roles.find((role) => parseInt(role.value) === parseInt(roleValue));
    return match ? match.name : 'Unknown';
  };

  const unassignedGroups = useMemo(
    () => groups.filter((group) => group.organization_id === null || group.organization_id === undefined || group.organization_id === ''),
    [groups]
  );

  const selectedRoleValue = parseInt(formData.role);
  const showGroupSelector = selectedRoleValue === 1 || selectedRoleValue > 2;

  const parseGroupIds = (value) => {
    let raw = value;
    if (typeof raw === 'string') {
      try {
        raw = JSON.parse(raw);
      } catch {
        return [];
      }
    }
    if (!Array.isArray(raw)) return [];
    return raw.map((id) => parseInt(id)).filter((id) => !Number.isNaN(id));
  };

  // Returns { names: string[], type: 'learner' | 'instructor' }
  const getUserGroupInfo = (user) => {
    const roleVal = parseInt(user.role);
    const isLearner = roleVal === 1;

    // Learners use group_ids; instructors / org-admins use instructor_group_ids
    const rawIds = isLearner ? user.group_ids : user.instructor_group_ids;
    const groupIds = parseGroupIds(rawIds);

    const names = groupIds
      .map((id) => groups.find((g) => parseInt(g.id) === id)?.name)
      .filter(Boolean);

    return { names, type: isLearner ? 'learner' : 'instructor' };
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  // Update handleFilterChange function
  const handleFilterChange = (event) => {
    setFilterStatus(event.target.value);
  };

  // Validate form data
  const validateForm = () => {
    const errors = {};
    
    if (!formData.first_name.trim()) {
      errors.first_name = 'First name is required';
    }
    
    if (!formData.last_name.trim()) {
      errors.last_name = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!editingUser && !formData.password.trim()) {
      errors.password = 'Password is required';
    } else if (!editingUser && formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    
    if (!formData.role) {
      errors.role = 'Role is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleShowModal = (user = null) => {
    if (user) {
      const roleValue = user.role?.toString() || '0';
      const isLearner = parseInt(roleValue) === 1;
      const existingGroupIds = parseGroupIds(isLearner ? user.group_ids : user.instructor_group_ids);

      setEditingUser(user);
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '', 
        email: user.email || '',
        password: '',
        role: roleValue,
        status: user.status,
        group_ids: existingGroupIds.map((id) => id.toString())
      });
    } else {
      setEditingUser(null);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        role: '',
        status: 1,
        group_ids: []
      });
    }
    setFormErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormErrors({});
    setModalLoading(false);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;

    if (name === 'role') {
      setFormData((prev) => ({
        ...prev,
        role: value,
        group_ids: []
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleGroupToggle = (groupId) => {
    const id = groupId.toString();
    setFormData((prev) => {
      const exists = prev.group_ids.includes(id);
      return {
        ...prev,
        group_ids: exists
          ? prev.group_ids.filter((value) => value !== id)
          : [...prev.group_ids, id]
      };
    });
  };

  const buildGroupPayload = () => {
    const numericGroupIds = formData.group_ids
      .map((id) => parseInt(id))
      .filter((id) => !Number.isNaN(id));

    if (selectedRoleValue === 1) {
      return { group_ids: numericGroupIds };
    }

    if (selectedRoleValue > 2) {
      return { instructor_group_ids: numericGroupIds };
    }

    return {};
  };

  const handleSave = async () => {
    try {
      setModalLoading(true);
      
      const userData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        role: parseInt(formData.role),
        status: parseInt(formData.status),
        ...(formData.password && { password: formData.password }),
        ...buildGroupPayload()
      };

      if (editingUser) {
        await api.users.update(editingUser.id, userData);
        toast.success('User updated successfully');
      } else {
        await api.users.create(userData);
        toast.success('User created successfully');
      }

      handleCloseModal();
      await fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error(error.response?.data?.message || 'Error saving user');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    setDeleteLoading(userToDelete.id);
    
    try {
      await api.users.delete(userToDelete.id);
      
      // Remove user from local state
      setUsers(prev => prev.filter(user => user.id !== userToDelete.id));
      
      toast.success(`User "${getUserDisplayName(userToDelete)}" deleted successfully`);
      
      // Refresh the users list to ensure we have the latest data
      await fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user: ' + (error.response?.data?.message || error.message));
    } finally {
      setDeleteLoading(null);
      setShowDeleteModal(false);
      setUserToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  // Update the filteredUsers logic
  const filteredUsers = users.filter(user => {
    const searchFields = [
      user.first_name || '',
      user.last_name || '',
      user.email || '',
      user.username || ''
    ].join(' ').toLowerCase();
    
    const matchesSearch = !searchTerm || searchFields.includes(searchTerm.toLowerCase());
    
    // Updated filter logic
    let matchesFilter = true;
    switch(filterStatus) {
      case 'active':
        matchesFilter = parseInt(user.status) === 1 && parseInt(user.deleted) === 0;
        break;
      case 'inactive':
        matchesFilter = parseInt(user.status) === 0 && parseInt(user.deleted) === 0;
        break;
      case 'deleted':
        matchesFilter = parseInt(user.deleted) === 1;
        break;
      default: // 'all'
        matchesFilter = parseInt(user.deleted) === 0; // Show only non-deleted users by default
        break;
    }
    
    return matchesSearch && matchesFilter;
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const getUserInitials = (user) => {
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    return (firstName[0] || '') + (lastName[0] || '');
  };

  const getUserDisplayName = (user) => {
    return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username || user.email;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-h-96 p-4">
        <Spinner animation="border" role="status" className="text-purple-600 w-12 h-12">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h5 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
          Manage User
        </h5>
      </div>
      
      {/* Search + Filter + Add */}
      <Row className="mb-3 g-2 sm:g-3">
        <Col xs={12} md={5} className="mb-2 mb-md-0">
          <InputGroup className="shadow-sm rounded-lg sm:rounded-xl overflow-hidden">
            <InputGroup.Text className="bg-white border-0 text-purple-600 px-3 sm:px-3">
              <FaSearch className="w-4 h-4" />
            </InputGroup.Text>
            <Form.Control
              placeholder="Search users by name, email, or username..."
              value={searchTerm}
              onChange={handleSearch}
              className="border-0 focus:ring-2 focus:ring-purple-500 text-sm sm:text-base"
            />
          </InputGroup>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Form.Select 
            value={filterStatus} 
            onChange={handleFilterChange}
            className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm"
          >
            <option value="all">All Users</option>
            <option value="active">Active Users</option>
            <option value="inactive">Inactive Users</option>
            <option value="deleted">Deleted Users</option>
          </Form.Select>
        </Col>
        <Col xs={12} md={4}>
          <div className="d-flex flex-row gap-2 h-full">
            <Button 
              bsPrefix="custom-hamburger"
              onClick={() => handleShowModal()}
              className="flex-fill border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
            >
              <FaPlus className="w-4 h-4" />
              <span className="whitespace-nowrap">Add User</span>
            </Button>
            <Button
              bsPrefix="custom-hamburger"
              onClick={() => setShowBulkUploadModal(true)}
              className="flex-fill border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
            >
              <FaUpload className="w-4 h-4" />
              <span className="whitespace-nowrap">Bulk Upload Users</span>
            </Button>
          </div>
        </Col>
      </Row>

      {/* No Data */}
      {filteredUsers.length === 0 ? (
        <Alert variant="info" className="text-center py-6 sm:py-8 rounded-xl sm:rounded-2xl bg-white/80 backdrop-blur shadow-md border border-purple-100">
          <p className="text-sm sm:text-base font-medium text-purple-700">
            {searchTerm || filterStatus !== 'all' 
              ? 'No users found matching your criteria.' 
              : 'No users found. Click "Add User" to create the first user.'
            }
          </p>
        </Alert>
      ) : (
        /* TABLE*/
        <div className="overflow-x-auto -mx-3 sm:mx-0 rounded-xl sm:rounded-2xl shadow-lg bg-white">
          <Table bordered hover className="m-0 min-w-max text-xs sm:text-sm">
            <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
              <tr>
                <th className="py-2 px-3 font-bold text-left">Name</th>
                <th className="py-2 px-3 font-bold text-left">Email/Username</th>
                <th className="py-2 px-3 font-bold text-left">Role</th>
                <th className="py-2 px-3 font-bold text-left">Group</th>
                <th className="py-2 px-3 font-bold text-left">2FA Enabled</th>
                <th className="py-2 px-3 font-bold text-left">Status</th>
                <th className="py-2 px-3 font-bold text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id} className="border-b border-purple-100 hover:bg-purple-50/50 transition-all">
                  <td className="py-2 px-3">
                    <div className="d-flex align-items-center gap-2">
                
                      <div>
                        <span className="fw-semibold text-gray-800 text-xs sm:text-sm">{getUserDisplayName(user)}</span>
                        {parseInt(user.deleted) === 1 && (
                          <Badge bg="danger" className="ms-2 text-xs">Deleted</Badge>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-2 px-3 text-gray-600 text-xs sm:text-sm break-all">{user.email}</td>
                  <td className="py-2 px-3 text-left">
                    <Badge bg={
                      parseInt(user.role) === 0 ? 'primary' : 
                      parseInt(user.role) === 1 ? 'secondary' : 
                      'warning'
                    } className="px-2 py-1 text-xs">
                      {getRoleName(user.role)}
                    </Badge>
                  </td>
                  <td className="py-2 px-3 text-left">
                    {(() => {
                      const { names, type } = getUserGroupInfo(user);
                      if (names.length === 0) {
                        return <span className="text-gray-400 text-xs">-</span>;
                      }
                      const badgeBg    = type === 'learner' ? 'info'    : 'warning';
                      const badgeTitle = type === 'learner' ? 'Learner' : 'Instructor';
                      return (
                        <div className="d-flex flex-wrap gap-1">
                          {names.map((name) => (
                            <Badge
                              key={name}
                              bg={badgeBg}
                              title={`${badgeTitle} Group`}
                              className="px-2 py-1 text-xs"
                            >
                              {name}
                            </Badge>
                          ))}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="py-2 px-3 text-left">
                    <div className="d-flex align-items-center gap-2">
                      <Form.Check
                        type="switch"
                        id={`twofa-${user.id}`}
                        label={Boolean(user.is_2fa_enabled) ? 'On' : 'Off'}
                        checked={Boolean(user.is_2fa_enabled)}
                        disabled={twofaLoadingUserId === user.id || parseInt(user.deleted) === 1}
                        onChange={() => handleToggle2fa(user)}
                      />
                      {twofaLoadingUserId === user.id ? (
                        <Spinner animation="border" size="sm" />
                      ) : null}
                    </div>
                  </td>
                  <td className="py-2 px-3 text-left">
                    <Badge bg={parseInt(user.status) === 1 ? 'success' : 'danger'} className="px-2 py-1 text-xs">
                      {parseInt(user.status) === 1 ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="py-2 px-3">
                    <div className="d-flex gap-1 justify-content-left">
                      <Button
                        bsPrefix="custom-hamburger"
                        size="sm"
                        onClick={() => handleShowModal(user)}
                        title="Edit User"
                        className="border-purple-300 text-purple-600 hover:bg-purple-50 rounded p-1 "
                      >
                        <FaEdit className="w-5 h-5" />
                      </Button>
                      {parseInt(user.deleted) === 0 ? (
                        <Button
                         bsPrefix="custom-hamburger"
                          size="sm"
                          onClick={() => handleDeleteClick(user)}
                          title="Delete User"
                          className="border-red-300 text-red-600 hover:bg-red-50 rounded p-1 "
                        >
                          <FaTrash className="w-5 h-5" />
                        </Button>
                      ) : (
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={async (e) => {
                            e.preventDefault();
                            try {
                              await api.users.restore(user.id);
                              toast.success(`User "${getUserDisplayName(user)}" has been restored`);
                              fetchUsers();
                            } catch (error) {
                              toast.error('Failed to restore user: ' + (error.response?.data?.message || error.message));
                            }
                          }}
                          title="Restore User"
                          className="border-green-300 text-green-600 hover:bg-green-50 rounded p-1 min-w-8 h-8"
                        >
                          <FaCheck className="w-5 h-5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* Add/Edit Modal - FULLSCREEN ON MOBILE */}
      <Modal 
        show={showModal} 
        onHide={handleCloseModal} 
        centered 
        fullscreen="sm-down"
        size="md"
        backdrop="static"
      >
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:py-4 sm:px-4">
          <Modal.Title className="font-bold text-base sm:text-lg">
            {editingUser ? `Edit User: ${getUserDisplayName(editingUser)}` : 'Add New User'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0 sm:p-5 bg-gray-50">
          <Form>
            <Row className="mb-3 g-2 sm:g-3">
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">First Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleFormChange}
                    isInvalid={!!formErrors.first_name}
                    placeholder="Enter first name"
                    className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.first_name}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">Last Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleFormChange}
                    isInvalid={!!formErrors.last_name}
                    placeholder="Enter last name"
                    className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.last_name}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">Email <span className="text-danger">*</span></Form.Label>
              <Form.Control
                name="email"
                type="email"
                value={formData.email}
                onChange={handleFormChange}
                isInvalid={!!formErrors.email}
                placeholder="Enter email address"
                disabled={editingUser}
                readOnly={editingUser}
                className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.email}
              </Form.Control.Feedback>
              {editingUser && (
                <Form.Text className="text-muted text-xs">
                  Email cannot be changed after user creation
                </Form.Text>
              )}
            </Form.Group>
            
            <Form.Group className="mb-3 position-relative">
              <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">
                Password {!editingUser && <span className="text-danger">*</span>}
                {editingUser && <small className="text-muted"> (leave blank to keep current password)</small>}
              </Form.Label>
              <Form.Control
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleFormChange}
                isInvalid={!!formErrors.password}
                placeholder={editingUser ? "Enter new password (optional)" : "Enter password"}
                minLength={6}
                className="rounded-lg border-purple-200 focus:ring-purple-500 pe-10 text-sm"
              />
              <a 
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setShowPassword(!showPassword);
                }}
                className="position-absolute end-0 top-1/2 -translate-y-1/2 pe-3 text-gray-500 hover:text-purple-600 z-10 mt-2 sm:mt-2 translate-y-px"
              >
                {showPassword ? <FaEyeSlash className="w-4 h-4" /> : <FaEye className="w-4 h-4" />}
              </a>
              <Form.Control.Feedback type="invalid">
                {formErrors.password}
              </Form.Control.Feedback>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">Role <span className="text-danger">*</span></Form.Label>
              <Form.Select
                name="role"
                value={formData.role}
                onChange={handleFormChange}
                isInvalid={!!formErrors.role}
                className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
              >
                <option value="">Select Role</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.value} disabled={parseInt(role.value) === 2}>
                    {role.name}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {formErrors.role}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">
                Groups <small className="text-muted">(optional)</small>
              </Form.Label>
              <div className={`border border-purple-200 rounded-lg p-3 bg-white max-h-48 overflow-y-auto ${!showGroupSelector ? 'opacity-75' : ''}`}>
                {!showGroupSelector ? (
                  <p className="text-muted text-sm mb-0">
                    Select Individual User or an Instructor role to assign groups.
                  </p>
                ) : unassignedGroups.length === 0 ? (
                  <p className="text-muted text-sm mb-0">No unassigned groups available.</p>
                ) : (
                  unassignedGroups.map((group) => {
                    const groupId = group.id.toString();
                    const inputId = `group-option-${groupId}`;
                    return (
                      <Form.Check
                        key={group.id}
                        type="checkbox"
                        id={inputId}
                        label={group.name}
                        checked={formData.group_ids.includes(groupId)}
                        onChange={() => handleGroupToggle(groupId)}
                        disabled={!showGroupSelector}
                        className="mb-2"
                      />
                    );
                  })
                )}
              </div>
              <Form.Text className="text-muted text-xs">
                Only groups not assigned to any organization are listed.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">Status</Form.Label>
              <Form.Select
                name="status"
                value={formData.status?.toString()}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    status: parseInt(e.target.value)
                  }));
                }}
                className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
              >
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 p-3 sm:p-4">
          <Row className="w-100 g-2">
            <Col xs={6}>
              <Button 
                bsPrefix="custom-hamburger"
                onClick={handleCloseModal} 
                disabled={modalLoading}
                className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"

              >
                Cancel
              </Button>
            </Col>
            <Col xs={6}>
              <Button 
                bsPrefix="custom-hamburger"
                onClick={handleSave} 
                disabled={modalLoading}
      className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2 "
              >
                {modalLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {editingUser ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  editingUser ? 'Update User' : 'Create User'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>

      <BulkUploadUsersModal
        show={showBulkUploadModal}
        onHide={() => setShowBulkUploadModal(false)}
        onImported={fetchUsers}
        roles={roles}
      />

      {/* Delete Modal - FULLSCREEN ON MOBILE */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered fullscreen="sm-down">
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 px-4 py-3 sm:px-4 sm:py-4">
          <Modal.Title className="text-sm sm:text-base">Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <div className="mb-3 flex justify-center items-center">
            <FaUserCircle size={50} className="text-gray-400" />
          </div>
          <h5 className="font-bold text-gray-800 text-sm sm:text-base">Delete User</h5>
          <p className="text-muted text-xs sm:text-sm">
            Are you sure you want to delete{' '}
            <strong>{userToDelete ? getUserDisplayName(userToDelete) : ''}</strong>?
          </p>
        </Modal.Body>
        <Modal.Footer className="border-0 p-3">
          <Row className="w-100 g-2">
            <Col xs={6}>
              <Button 
                bsPrefix="custom-hamburger"
                onClick={handleDeleteCancel}
                disabled={deleteLoading}
                className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                Cancel
              </Button>
            </Col>
            <Col xs={6}>
              <Button 
                bsPrefix="custom-hamburger"
                onClick={handleDeleteConfirm}
                disabled={deleteLoading}
                className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                {deleteLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Deleting...
                  </>
                ) : (
                  'Delete User'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UserManagement;